import { useState, useEffect, useRef } from 'react';
import { ControllerRenderProps, FieldValues, useController, useFormContext } from 'react-hook-form';
import { Input, InputProps } from '@/components/ui/input';
import { FormField, FormItem } from './ui/form';
import { Check, CirclePlus, Search, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UseQueryOptions } from '@tanstack/react-query';
import Loader from './Loader';
import { Label } from './ui/label';

interface Option {
  label: string;
  value: string;
  availability?: number;
  displayLabel?: string;
  option?: object;
  secondaryLabel?: string;
}

interface FormFieldSearchProps<TParams> {
  name: string;
  label: string;
  placeholder: string;
  props: InputProps;
  required?: boolean;
  showFilter?: boolean;
  showFilterList?: Option[];
  availability?: boolean;
  labelChange?: boolean;
  multiselect?: boolean;
  disabled?: boolean;
  addNewOption?: {
    label: string;
    onNewOptionClick: () => void;
  };
  onChange?: (value: string, option?: object) => void;
  onSearchChange?: (value: string) => void;
  searchFieldName?: string;
  api?: (
    params: TParams,
    options?: { query?: UseQueryOptions }
  ) => {
    values: Option[];
    isLoading: boolean;
    refetch?: () => void;
  };
  errorMessage?: string;
  onBlur?: (value: Option | undefined) => void;
  tooltip?: string;
}

const FormFieldSearch = <TParams extends { search?: string }>({
  name,
  label,
  placeholder,
  props,
  required,
  showFilter,
  showFilterList,
  labelChange,
  onChange,
  onSearchChange,
  disabled,
  multiselect = false,
  api,
  searchFieldName = 'search',
  addNewOption,
  errorMessage,
  onBlur,
  tooltip,
}: FormFieldSearchProps<TParams>) => {
  const { control, setValue } = useFormContext<Record<string, string | string[]>>();
  const { field } = useController({ control, name });
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const handleOptionClick = (option: Option, field: ControllerRenderProps<FieldValues, string>) => {
    if (multiselect) {
      const newSelectedValues = selectedValues.includes(option.value)
        ? selectedValues.filter((val) => val !== option.value)
        : [...selectedValues, option.value];
      setSelectedValues(newSelectedValues);
      field?.onChange(newSelectedValues);
      if (onChange) {
        onChange(option.value, option.option);
      }
    } else {
      setInputValue(option.displayLabel ?? option.label);
      field.onChange(option.value);
      setIsOpen(false);
      if (onChange) {
        onChange(option.value, option.option);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        setActiveIndex((prev) => (prev + 1) % values.length);
        break;
      case 'ArrowUp':
        setActiveIndex((prev) => (prev - 1 + values.length) % values.length);
        break;
      case 'Enter':
        event.preventDefault();
        if (values[activeIndex]) {
          handleOptionClick(values[activeIndex], field);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const getPlaceholder = () => {
    if (multiselect && selectedValues.length > 0) {
      return selectedValues.join(', ');
    }
    return placeholder;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const params = {
    [searchFieldName]: textSearch?.length ? textSearch : undefined,
  } as TParams;

  const { values, isLoading } = api ? api(params) : { values: [], isLoading: false };

  useEffect(() => {
    const interval = setTimeout(() => {
      setTextSearch(inputValue);
    }, 300);

    return () => {
      clearTimeout(interval);
    };
  }, [inputValue]);

  useEffect(() => {
    if (!field.value) setInputValue('');
  }, [field.value]);

  useEffect(() => {
    if (field.value && !multiselect) {
      const option = values?.find((d) => d.value === field.value);
      if (option) {
        setInputValue(option.displayLabel ?? option.label);
      }
    }
    if (multiselect && field.value) {
      setSelectedValues(field.value as string[]);
    }
  }, [values]);

  return (
    <FormItem className="flex flex-col gap-1">
      <Label htmlFor={name} className="block text-sm font-medium">
        {labelChange ? `${label} ${showFilterList?.find((sfl) => sfl.value === filter)?.label}` : label}
        {required && <span className="text-error align-top">*</span>}
        {tooltip && (
          <div className="group relative inline-flex items-center ml-1">
            <span className="cursor-pointer text-muted-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-info"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
            </span>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden w-max max-w-xs rounded bg-popover px-2 py-1 text-sm text-popover-foreground shadow-md group-hover:block z-50 border">
              {tooltip}
            </div>
          </div>
        )}
      </Label>
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <>
            <div
              className="relative"
              ref={dropdownRef}
              onBlur={() => {
                setIsOpen(false);
                if (!field.value) {
                  setTextSearch('');
                }
                field.onBlur();
              }}
              onKeyDown={handleKeyDown}
            >
              <div
                className={`flex flex-row rounded-md border-[1px] ${isOpen ? '!border-focuscolor' : 'border-bordercolor'} justify-between pr-2 h-[48px]`}
              >
                {multiselect ? (
                  <div
                    className="flex flex-row rounded-md gap-2 pl-[10px] py-[14px] h-12 w-[85%]"
                    onFocus={() => {
                      setIsOpen(true);
                    }}
                    onClick={() => {
                      inputRef.current?.focus();
                    }}
                  >
                    <div className="flex justify-center align-middle items-center">
                      <Search className="text-primary h-[20px] w-[20px] text-[#0A0A0A]" />
                    </div>
                    <div className="flex flex-row p-0  overflow-x-scroll scrollbar-hide max-w-full border-none">
                      <span className="whitespace-nowrap flex items-center">
                        {multiselect &&
                          selectedValues.length > 0 &&
                          values
                            .filter((d) => selectedValues.includes(d.value))
                            .map((d) => d.label)
                            .join(', ')}
                      </span>

                      <div className="flex-1">
                        <Input
                          {...field}
                          onFocus={() => {
                            setIsOpen(true);
                          }}
                          disabled={disabled}
                          ref={inputRef}
                          onChange={(event) => {
                            const value = event.target.value;
                            setIsOpen(true);
                            setTextSearch(value);
                            field.onChange(value);
                            onSearchChange && onSearchChange(value);
                          }}
                          className="w-[100%] p-0 flex h-full border-none min-w-[40px] focus:outline-none focus:ring-0"
                          placeholder={selectedValues.length === 0 ? placeholder : ''}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <Input
                    value={inputValue}
                    placeholder={getPlaceholder()}
                    onFocus={() => {
                      setIsOpen(true);
                    }}
                    disabled={disabled}
                    onChange={(event) => {
                      const value = event.target.value;
                      setInputValue(value);
                      setIsOpen(true);
                      onSearchChange && onSearchChange(value);
                      if (value.length === 0) {
                        setValue(name, '', { shouldDirty: true });
                      } else {
                        setValue(name, value, { shouldDirty: true });
                      }
                    }}
                    onBlur={() => {
                      const selectedValue = values.find((value) => value.value === field.value);
                      if (!selectedValue) setValue(name, '', { shouldDirty: false });
                      field.onBlur();
                      onBlur?.(selectedValue);
                    }}
                    className="w-full border-none focus:border-[1px] focus:border-blue-500 focus:ring-0 focus:outline-none"
                    {...props}
                  />
                )}
                {showFilter && showFilterList && (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      className=" bg-transparent !border-[0px] pl-[10px] py-[14px] h-12 !text-primary font-[500] text-sm"
                    >
                      <div className="flex justify-center h-full text-center items-center w-6 ">
                        <Settings2 color="#3B82F6" className="cursor-pointer flex h-6 w-6" />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-w-56  w-fit mr-[150px]">
                      {showFilterList?.map((option) => (
                        <DropdownMenuCheckboxItem
                          key={option.value}
                          className={`text-placeholder ${filter === option.value && 'text-primary bg-[#EDF5FF]'}`}
                          checked={filter === option.value}
                          onClick={() => setFilter(option.value)}
                        >
                          {option.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              {isOpen && (
                <div className="absolute z-10 w-full bg-white shadow-md rounded h-fit max-h-[300px] overflow-y-scroll scrollbar-hide">
                  <Loader isLoading={isLoading} className="min-h-[100px]">
                    <ul>
                      {values.length > 0 ? (
                        <>
                          {values?.map((option, index) => (
                            <li
                              key={option.value}
                              className={`cursor-pointer break-all text-sm hover:bg-[#EDF5FF] p-2 flex items-center rounded gap-2 ${selectedValues.includes(option.value) || field.value === option.value
                                ? 'bg-[#EDF5FF] font-[500] text-primary'
                                : ' text-[#676767]'
                                } ${index === activeIndex ? 'font-[500] text-primary bg-[#EDF5FF]' : ''}`}
                              onMouseDown={() => handleOptionClick(option, field)}
                            >
                              <div>
                                <Check
                                  strokeWidth="2"
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    selectedValues.includes(option.value) || field.value === option.value
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-[500] text-sm">{option.label}</span>
                                {option.secondaryLabel && (
                                  <span className="text-[#676767] text-xs">{option.secondaryLabel}</span>
                                )}
                                {option?.availability && option.availability !== undefined && (
                                  <span
                                    className={`font-[400] ${option?.availability === 0
                                      ? 'text-[#37AE74]'
                                      : option?.availability > 0 && option?.availability < 4
                                        ? 'text-[#FFAD0D]'
                                        : 'text-[#D72222]'
                                      }`}
                                  >
                                    {option?.availability === 0
                                      ? 'Available'
                                      : `Current task ends in next ${option?.availability} days *`}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </>
                      ) : (
                        <li className={`cursor-pointer text-sm hover:bg-[#EDF5FF] p-2 flex items-center rounded `}>
                          <span className="p-2 w-full justify-center">{errorMessage ?? 'No results found'}</span>
                        </li>
                      )}
                    </ul>
                    {addNewOption && (
                      <div
                        className="pl-8 justify-center cursor-pointer flex flex-col sticky bg-white h-[32px] bottom-0 gap-4 hover:bg-[#EDF5FF]"
                        onMouseDown={() => {
                          addNewOption.onNewOptionClick();
                        }}
                      >
                        <div className="flex items-center flex-row gap-2 font-[400]">
                          <CirclePlus className="w-4 h-4" color="#1B8652" />
                          <span className="text-black text-sm font-normal">{addNewOption.label}</span>
                        </div>
                      </div>
                    )}
                  </Loader>
                </div>
              )}
            </div>
            {fieldState.error && <p className="text-error text-xs">{fieldState.error.message}</p>}
          </>
        )}
      />
    </FormItem>
  );
};

export default FormFieldSearch;
